import {DatePipe} from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {Observable, Subscription} from 'rxjs';
import {TuiDialogService} from '@taiga-ui/core';
import {PolymorpheusComponent} from '@taiga-ui/polymorpheus';
import {TranslateService} from '@ngx-translate/core';
import {GateweeTableColumn} from '@gatewee/interfaces';
import {GateweeConfirmDialogComponent} from '../gatewee-confirm-dialog/gatewee-confirm-dialog.component';
import {RecipientKey} from '@gatewee/types/recipient';
import {JoinArrayPipe} from '@gatewee/utils/pipes/join-array.pipe';

export interface CreateRecipientKeyPayload {
  description?: string;
  allowList: string[];
  sendToMaster?: boolean;
}

export interface ApiKeyActions {
  onCreate?: (
    payload: CreateRecipientKeyPayload,
  ) => Observable<RecipientKey & {secret?: string}>;
  onDisable?: (id: number) => Observable<void>;
  onView?: (key: RecipientKey) => void;
  loadKeys?: () => void;
}

function validateCIDRList(control: {value: string}) {
  if (!control.value) return null;
  const ips = control.value.split(',').map((ip: string) => ip.trim());
  const cidrRegex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;
  for (const ip of ips) {
    if (!cidrRegex.test(ip)) {
      return {invalidCIDR: true};
    }
  }
  return null;
}

@Component({
  selector: 'gatewee-api-keys',
  templateUrl: './gatewee-api-keys.component.html',
  styleUrls: ['./gatewee-api-keys.component.css'],
  standalone: false,
  providers: [DatePipe, JoinArrayPipe],
})
export class GateweeApiKeysComponent implements OnInit {
  @Input() keys: RecipientKey[] = [];
  @Input() actions!: ApiKeyActions;
  @Input() disableContentPage = false;
  @Input() breadcrumb: {label: string; path?: string}[] = [];
  @Input() title = 'features.apiKeys.title';
  @Input() titlePath = 'features.apiKeys.titlePath';
  @Input() page = 1;
  @Input() pageSize = 10;
  @Input() total = 0;
  @Output() pageChange = new EventEmitter<number>();

  @ViewChild('createKeyModal') createKeyModal!: TemplateRef<never>;
  @ViewChild('keyViewModal') keyViewModal!: TemplateRef<never>;

  columns: GateweeTableColumn[] = [];
  createForm!: FormGroup;
  selectedKey: RecipientKey | null = null;
  loading = false;
  /** Shown only after create when the API returned a one-time secret */
  postCreateReveal = false;
  /** Copy of the secret for the reveal UI; cleared when the details modal closes */
  ephemeralSecret: string | null = null;

  private createModalSub?: Subscription;
  private keyViewModalSub?: Subscription;

  constructor(
    private dialogService: TuiDialogService,
    private fb: FormBuilder,
    private datePipe: DatePipe,
    private joinArrayPipe: JoinArrayPipe,
    private translate: TranslateService,
  ) {
    this.createForm = this.fb.group({
      description: [''],
      allowList: ['0.0.0.0/0', [Validators.required, validateCIDRList]],
      sendToMaster: [false],
    });
  }

  ngOnInit(): void {
    this.setupColumns();
  }

  private setupColumns(): void {
    this.columns = [
      {
        label: 'features.apiKeys.table.columns.id',
        field: 'id',
        sortable: false,
        visible: true,
      },
      {
        label: 'features.apiKeys.table.columns.key',
        field: 'key',
        sortable: false,
        visible: true,
      },
      {
        label: 'features.apiKeys.table.columns.publicKey',
        field: 'publicKey',
        sortable: false,
        visible: true,
      },
      {
        label: 'features.apiKeys.table.columns.description',
        field: 'description',
        sortable: false,
        visible: true,
        pipe: {
          fn: {
            transform: (v: string) => v || 'N/A',
          },
          args: [],
        },
      },
      {
        label: 'features.apiKeys.table.columns.enable',
        field: 'enabled',
        sortable: false,
        visible: true,
        pipe: {
          fn: {
            transform: (v: boolean) =>
              v
                ? 'features.apiKeys.status.true'
                : 'features.apiKeys.status.false',
          },
          args: [],
        },
      },
      {
        label: 'features.apiKeys.table.columns.allowList',
        field: 'allowList',
        sortable: false,
        visible: true,
        pipe: {
          fn: this.joinArrayPipe,
          args: [', '],
        },
      },
      {
        label: 'features.apiKeys.table.columns.createdAt',
        field: 'createdAt',
        sortable: false,
        visible: true,
        pipe: {
          fn: this.datePipe,
          args: ['dd/MM/yyyy HH:mm:ss'],
        },
      },
      {
        label: 'features.apiKeys.table.columns.lastActivity',
        field: 'lastActivity.createdAt',
        sortable: false,
        visible: true,
        pipe: {
          fn: {
            transform: (v: unknown) =>
              v !== null && v !== undefined
                ? this.datePipe.transform(
                    v as string | number | Date,
                    'dd/MM/yyyy HH:mm:ss',
                  )
                : 'N/A',
          },
          args: [],
        },
      },
      {
        label: 'features.apiKeys.table.columns.actions',
        field: 'actions',
        sortable: false,
        visible: true,
        alwaysVisible: true,
        actions: [
          {
            key: 'view',
            label: 'common.actions.view',
            icon: '@tui.eye',
          },
        ],
      },
    ];
  }

  onActionClicked(event: {row: RecipientKey; type: string}): void {
    const {row, type} = event;
    switch (type) {
      case 'view':
        this.openKeyView(row);
        break;
      case 'disable':
        this.handleDisable(row.id);
        break;
      default:
        break;
    }
  }

  private clearKeyViewState(): void {
    this.selectedKey = null;
    this.ephemeralSecret = null;
    this.postCreateReveal = false;
    this.actions.loadKeys?.();
  }

  private subscribeKeyViewModal(): void {
    this.keyViewModalSub?.unsubscribe();
    this.keyViewModalSub = this.dialogService
      .open(this.keyViewModal, {
        size: 'l',
        closeable: true,
      })
      .subscribe({
        complete: () => {
          this.keyViewModalSub = undefined;
          this.clearKeyViewState();
        },
      });
  }

  /** Taiga template dialogs: `let-close` may be a function or an observer with `complete()`. */
  closeCreateModal(close: unknown): void {
    if (typeof close === 'function') {
      (close as () => void)();
    } else if (
      close &&
      typeof close === 'object' &&
      'complete' in close &&
      typeof (close as {complete?: () => void}).complete === 'function'
    ) {
      (close as {complete: () => void}).complete();
    }
    this.createModalSub?.unsubscribe();
    this.createModalSub = undefined;
  }

  private closeKeyViewDialog(close: unknown): void {
    if (typeof close === 'function') {
      (close as () => void)();
    } else if (
      close &&
      typeof close === 'object' &&
      'complete' in close &&
      typeof (close as {complete?: () => void}).complete === 'function'
    ) {
      (close as {complete: () => void}).complete();
    }
  }

  openKeyView(key: RecipientKey): void {
    if (this.actions.onView) {
      this.actions.onView(key);
      return;
    }
    this.postCreateReveal = false;
    this.ephemeralSecret = null;
    this.selectedKey = key;
    this.subscribeKeyViewModal();
  }

  openCreateModal(): void {
    this.createForm.reset({
      description: '',
      allowList: '0.0.0.0/0',
      sendToMaster: false,
    });
    this.createModalSub?.unsubscribe();
    this.createModalSub = this.dialogService
      .open(this.createKeyModal, {
        size: 'm',
        closeable: true,
      })
      .subscribe({
        complete: () => {
          this.createModalSub = undefined;
          this.createForm.reset({
            description: '',
            allowList: '0.0.0.0/0',
            sendToMaster: false,
          });
        },
      });
  }

  handleCreate(close: unknown): void {
    if (this.createForm.invalid || !this.actions.onCreate) {
      return;
    }
    const value = this.createForm.value;
    const allowList = value.allowList
      .split(',')
      .map((ip: string) => ip.trim())
      .filter(Boolean);
    const payload: CreateRecipientKeyPayload = {
      description: value.description || undefined,
      allowList,
      sendToMaster: value.sendToMaster,
    };
    this.loading = true;
    this.actions.onCreate(payload).subscribe({
      next: (key: RecipientKey & {secret?: string}) => {
        this.loading = false;
        const raw = key as unknown as Record<string, unknown>;
        const secretRaw = raw['secret'];
        const rk = key as RecipientKey;
        this.selectedKey = {
          ...rk,
          allowList: Array.isArray(rk.allowList) ? rk.allowList : [],
          key: String(rk.key ?? ''),
          secret: String(secretRaw ?? rk.secret ?? ''),
        } as RecipientKey;
        this.ephemeralSecret =
          secretRaw !== undefined &&
          secretRaw !== null &&
          String(secretRaw).length > 0
            ? String(secretRaw)
            : null;
        this.postCreateReveal = !!this.ephemeralSecret;
        this.closeCreateModal(close);
        setTimeout(() => this.subscribeKeyViewModal(), 0);
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  copyToClipboard(text: string): void {
    void navigator.clipboard.writeText(text);
  }

  copyAllCredentials(): void {
    if (!this.selectedKey) return;
    const lines: string[] = [];
    lines.push(
      `${this.translate.instant('features.apiKeys.view.copyLabelKey')}: ${
        this.selectedKey.key
      }`,
    );
    if (this.selectedKey.publicKey) {
      lines.push(
        `${this.translate.instant(
          'features.apiKeys.view.copyLabelPublicKey',
        )}: ${this.selectedKey.publicKey}`,
      );
    }
    if (this.ephemeralSecret) {
      lines.push(
        `${this.translate.instant('features.apiKeys.view.copyLabelSecret')}: ${
          this.ephemeralSecret
        }`,
      );
    }
    void navigator.clipboard.writeText(lines.join('\n'));
  }

  handleDisable(id: number): void {
    if (!this.actions.onDisable) return;
    this.dialogService
      .open<boolean>(new PolymorpheusComponent(GateweeConfirmDialogComponent), {
        data: {
          title: this.translate.instant(
            'features.apiKeys.disableConfirm.title',
          ),
          message: this.translate.instant(
            'features.apiKeys.disableConfirm.message',
          ),
          confirmLabel: this.translate.instant(
            'features.apiKeys.table.menu.disable',
          ),
          cancelLabel: this.translate.instant('common.actions.cancel'),
        },
        dismissible: true,
      })
      .subscribe(confirmed => {
        if (confirmed) {
          this.actions.onDisable!(id).subscribe({
            next: () => {},
            error: () => {},
          });
        }
      });
  }

  onDisableInView(close: unknown): void {
    if (!this.selectedKey || !this.actions.onDisable) return;
    this.loading = true;
    this.actions.onDisable(this.selectedKey.id).subscribe({
      next: () => {
        this.loading = false;
        this.actions.loadKeys?.();
        this.closeKeyViewDialog(close);
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  onPageChange(page: number): void {
    this.pageChange.emit(page);
  }

  getLastActivityDisplay(key: RecipientKey): string {
    if (!key.lastActivity?.createdAt) {
      return 'N/A';
    }
    return (
      this.datePipe.transform(
        key.lastActivity.createdAt,
        'dd/MM/yyyy HH:mm:ss',
      ) ?? 'N/A'
    );
  }
}
